<template>
  <FriedModal :size="ModalSize.Large" :closable="false">
    <div class="intersection-subheader">
      <div class="intersection-logo">
        <FriedGetAcceptLogo class="intersection-logo" />
        <FriedIconButton data-external="onboarding-modal-close-button" @click="handleDismiss">
          <FriedIcon :size="IconSize.Small" icon="close" />
        </FriedIconButton>
      </div>
      <FriedParagraph>{{ onboardingWelcomeText }}</FriedParagraph>
      <FriedH2>{{ onboardingSubheaderText }}</FriedH2>
    </div>
    <div class="intersection">
      <div
        v-if="!features[Feature.GlobalTemplateLibrary].restricted"
        class="onboarding-card-container"
      >
        <TemplateThumbnail class="intersection-thumbnail" />
        <FriedH3>{{ $t('onboarding-start-template') }}</FriedH3>
        <FriedParagraph class="description">
          {{ $t('onboarding-template-description') }}
        </FriedParagraph>
        <FriedButton
          id="onboarding-fork-template"
          class="onboarding-button"
          :button-type="ButtonType.Secondary"
          data-external="onboarding-modal-template-option"
          @click="handleGoTemplateGallery"
        >
          {{ $t('onboarding-template-library') }}
        </FriedButton>
      </div>
      <div class="onboarding-card-container">
        <DocumentThumbnail class="intersection-thumbnail" />
        <FriedH3>{{ $t('onboarding-start-document') }}</FriedH3>
        <FriedParagraph class="description">{{
          $t('onboarding-document-description')
        }}</FriedParagraph>
        <FriedButton
          id="onboarding-fork-upload"
          class="onboarding-button"
          :button-type="ButtonType.Secondary"
          :loading="creating"
          data-external="onboarding-modal-document-option"
          @click="handleDocumentOnboarding"
        >
          {{ $t('onboarding-upload-doc') }}
        </FriedButton>
      </div>
    </div>
  </FriedModal>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { ButtonType, ModalSize, IconSize } from '@getaccept/fried-tofu';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import {
  AnalyticsEvent,
  AnalyticsOnboardingChoice,
  AnalyticsUserTrait,
} from '@getaccept/lib-shared/src/types/analytics';
import Analytics from '@getaccept/lib-shared/src/analytics';
import confetti from 'canvas-confetti';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useToast } from '@getaccept/lib-shared/src/toast';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import TemplateThumbnail from '../../assets/onboarding/template-thumbnail.svg';
import DocumentThumbnail from '../../assets/onboarding/document-thumbnail.svg';
import { DocumentService } from '../../api/documents/services/document.service';

export default defineComponent({
  components: {
    DocumentThumbnail,
    TemplateThumbnail,
  },
  props: { userHash: { type: String, default: '' } },
  emits: ['documentCreated', 'close'],
  setup(props, { emit }) {
    const { toast } = useToast();
    const creating = ref(false);
    const redirectedFromSetupWizard = ref(false);

    const { features } = storeToRefs(useFeatureStore());

    const onboardingWelcomeText = computed(() =>
      redirectedFromSetupWizard.value ? t('onboarding-welcome-setup') : t('onboarding-welcome')
    );

    const onboardingSubheaderText = computed(() =>
      redirectedFromSetupWizard.value ? t('onboarding-subheader-setup') : t('onboarding-subheader')
    );

    const handleDocumentOnboarding = async () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Upload);
      await createDocument();
    };

    const createDocument = async () => {
      creating.value = true;
      try {
        const id: string = await DocumentService.createDocument(EnumDocumentType.Document);
        emit('documentCreated', { id, type: EnumDocumentType.Document });
        emit('close');
      } catch (e) {
        toast.danger(t('could-not-create-document'));
      } finally {
        creating.value = false;
      }
    };

    const router = useRouter();

    const handleGoTemplateGallery = () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Template);
      emit('close');
      router.push({
        path: '/content-library',
        query: { type: 'intro' },
      });
    };

    const handleDismiss = () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Dismiss);
      emit('close');
      router.replace(PathName.Dashboard);
    };

    const handleAnalyticsOnboarding = (choice: AnalyticsOnboardingChoice) => {
      Analytics.identify(props.userHash, {
        [AnalyticsUserTrait.OnboardingStep]: 1,
        [AnalyticsUserTrait.OnboardingChoice]: choice,
      });
      Analytics.track(AnalyticsEvent.OnboardingStep1, {
        [AnalyticsUserTrait.OnboardingStep]: 1,
        [AnalyticsUserTrait.OnboardingChoice]: choice,
      });
    };

    const fire = (
      particleRatio: number,
      opts: { spread: number; startVelocity?: number; decay?: number; scalar?: number }
    ) => {
      confetti(
        Object.assign({}, { origin: { y: 0.7 } }, opts, {
          zIndex: 2147483003,
          particleCount: Math.floor(300 * particleRatio),
        })
      );
    };

    const showConfetti = () => {
      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });
      fire(0.2, {
        spread: 60,
      });
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
    };

    const handleAnalyticsTracking = () => {
      Analytics.identify(props.userHash, {
        [AnalyticsUserTrait.OnboardingStep]: 0,
      });
      Analytics.track(AnalyticsEvent.OnboardingLoaded, {
        [AnalyticsUserTrait.OnboardingStep]: 0,
      });
    };

    const route = useRoute();
    onMounted(() => {
      showConfetti();
      handleAnalyticsTracking();
      redirectedFromSetupWizard.value = route.query.intro === 'setup';
    });

    return {
      ButtonType,
      ModalSize,
      creating,
      redirectedFromSetupWizard,
      IconSize,
      onboardingWelcomeText,
      onboardingSubheaderText,
      handleDocumentOnboarding,
      createDocument,
      handleGoTemplateGallery,
      handleDismiss,
      handleAnalyticsOnboarding,
      fire,
      showConfetti,
      handleAnalyticsTracking,
      features,
      Feature,
    };
  },
});
</script>

<style lang="scss" scoped>
.intersection-subheader {
  color: var(--text-dark);

  p {
    padding-bottom: var(--spacing-25);
  }

  h2 {
    padding-bottom: var(--spacing-150);
  }

  .intersection-logo {
    display: flex;
    justify-content: space-between;
    height: 2.25rem;
    margin-bottom: var(--spacing-150);
  }
}

.intersection {
  display: flex;
  flex-direction: row;
  gap: var(--spacing-200);

  .onboarding-card-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border: 2px dashed var(--gray-60);
    border-radius: var(--radius-large);
    padding: 0 var(--spacing-200);

    .intersection-thumbnail {
      padding: var(--spacing-150) 0;
      width: 150px;
    }

    h3 {
      padding-bottom: var(--spacing-50);
      text-align: center;
    }

    .description {
      text-align: center;
      padding-bottom: var(--spacing-200);
      color: var(--text-gray);
    }

    .onboarding-button {
      width: fit-content;
      margin-bottom: var(--spacing-200);
    }
  }

  @media screen and (max-width: $xs) {
    flex-direction: column;
    gap: var(--spacing-100);

    .onboarding-card-container {
      h3 {
        padding-top: var(--spacing-150);
      }

      .intersection-thumbnail {
        display: none;
      }
    }
  }
}
</style>
